import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  FormHelperText, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { AuthService } from '../../../services/auth.service';
import { AccountService } from '../../../services/account.service';
import { IntercompanyService } from '../../../services/intercompany.service';
import { InstitutionGroupService } from '../../../services/institutionGroup.service';
import { PERMISSIONS } from '../../../constants/permissions';
import {
  IcKind, IcMap, IcTxn, InstitutionGroup, Reconciliation,
} from '../../../interfaces/intercompany.interface';
import { Account } from '../../../interfaces/accounting.interface';
import { SearchableSelect } from '../../common/SearchableSelect';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import FocusedRowChip from '../../common/FocusedRowChip';
import { useFocusedRow } from '../../../hooks/useFocusedRow';
import { depositAccountOptions, leafAccounts } from '../../../utils/postingAccounts';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../../utils/format';

const KINDS: IcKind[] = ['SALE', 'RECHARGE', 'LOAN', 'SETTLEMENT'];
const STATUSES = ['PENDING_ACCEPTANCE', 'POSTED', 'REJECTED', 'REJECTED_BY_COUNTERPARTY', 'REVERSED'];
const COLORS: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  PENDING_ACCEPTANCE: 'warning', POSTED: 'success', REJECTED: 'error', REJECTED_BY_COUNTERPARTY: 'error', REVERSED: 'default',
};
const asList = (r: unknown): Account[] => (Array.isArray(r) ? r : ((r as { content?: Account[] } | undefined)?.content ?? []));
const bankKind = (k: IcKind | '') => k === 'LOAN' || k === 'SETTLEMENT';
const money = (v: number | null | undefined) => (v === null || v === undefined ? '—' : formatMoney(v));

type Dlg =
  | { kind: 'initiate' }
  | { kind: 'accept' | 'reject' | 'reverse' | 'withdraw'; t: IcTxn };

const Intercompany: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.INTERCOMPANY_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [tab, setTab] = useState(0);
  const [group, setGroup] = useState<InstitutionGroup | null>(null);
  const [groupLoaded, setGroupLoaded] = useState(false);
  const [rows, setRows] = useState<IcTxn[]>([]);
  const [maps, setMaps] = useState<IcMap[]>([]);
  const [recon, setRecon] = useState<Reconciliation | null>(null);
  const [reconAsOf, setReconAsOf] = useState(toLocalIsoDate());
  const [status, setStatus] = useState('');
  const { focusId, clear: clearFocus } = useFocusedRow();
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState<Dlg | null>(null);
  const [saving, setSaving] = useState(false);
  const [assets, setAssets] = useState<Account[]>([]);
  const [liabilities, setLiabilities] = useState<Account[]>([]);
  const [revenues, setRevenues] = useState<Account[]>([]);
  const [expenses, setExpenses] = useState<Account[]>([]);

  // initiate / action form
  const [to, setTo] = useState('');
  const [kind, setKind] = useState<IcKind | ''>('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [description, setDescription] = useState('');
  const [fromAccountId, setFromAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [postBoth, setPostBoth] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [reason, setReason] = useState('');
  // the user picked the revenue account themselves, so the map default must not overwrite it
  const fromTouched = useRef(false);

  const counterparties = useMemo(
    () => (group?.members ?? []).filter(m => m.institutionId !== institutionId), [group, institutionId]);

  // The account pickers read these lists; "Create suggested accounts" adds accounts, so the lists are refetched after it.
  const loadAccounts = useCallback(async () => {
    try {
      const [a, l, r, x] = await Promise.all([
        AccountService.getAccountsByType('ASSET'), AccountService.getAccountsByType('LIABILITY'),
        AccountService.getAccountsByType('REVENUE'), AccountService.getAccountsByType('EXPENSE'),
      ]);
      setAssets(leafAccounts(asList(a))); setLiabilities(leafAccounts(asList(l)));
      setRevenues(leafAccounts(asList(r))); setExpenses(leafAccounts(asList(x)));
    } catch (e) {
      toast.showApiError(e, 'Failed to load accounts');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTxns = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await IntercompanyService.listTxns(institutionId, status || undefined));
    } catch (e) {
      toast.showApiError(e, 'Failed to load inter-company transactions');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        setGroup(await InstitutionGroupService.mine(institutionId));
      } catch (e) {
        toast.showApiError(e, 'Failed to load the institution group');
      } finally {
        setGroupLoaded(true);
      }
    })();
    loadAccounts();
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tab === 0) loadTxns(); }, [tab, loadTxns]);
  const loadMaps = useCallback(async () => {
    try { setMaps(await IntercompanyService.listMaps(institutionId)); } catch (e) { toast.showApiError(e, 'Failed to load the account map'); }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 1) loadMaps(); }, [tab, loadMaps]);
  const loadRecon = useCallback(async () => {
    try { setRecon(await IntercompanyService.reconciliation(institutionId, reconAsOf)); } catch (e) { toast.showApiError(e, 'Failed to load the reconciliation'); }
  }, [institutionId, reconAsOf]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 2) loadRecon(); }, [tab, loadRecon]);

  // Recharge / sale: prefill the revenue account from the account map of the chosen counterparty.
  useEffect(() => {
    if (dlg?.kind !== 'initiate' || !to || !kind || bankKind(kind) || fromTouched.current) return;
    const m = maps.find(x => x.counterpartyInstitutionId === to);
    setFromAccountId(m?.revenueAccountId ?? '');
  }, [dlg, to, kind, maps]);

  const open = (d: Dlg) => {
    setDlg(d); setReason(''); setToAccountId('');
    if (d.kind === 'initiate') {
      fromTouched.current = false;
      if (maps.length === 0) loadMaps();
      setTo(''); setKind(''); setAmount(''); setDate(toLocalIsoDate()); setDescription(''); setFromAccountId(''); setPostBoth(false);
      setRequestId(typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : '');
    }
  };

  const valid = (() => {
    if (!dlg) return false;
    if (dlg.kind === 'initiate') return !!to && !!kind && Number(amount) > 0 && !!date && (!bankKind(kind) || !!fromAccountId);
    if (dlg.kind === 'accept') return !bankKind(dlg.t.kind) || !!toAccountId;
    if (dlg.kind === 'withdraw') return true;
    return reason.trim().length > 0;
  })();

  const submit = async () => {
    if (!dlg) return;
    try {
      setSaving(true);
      if (dlg.kind === 'initiate') {
        await IntercompanyService.initiate({
          fromInstitutionId: institutionId, toInstitutionId: to, kind: kind as IcKind, txnDate: date, amount: Number(amount),
          description: description || undefined, fromAccountId: fromAccountId || undefined,
          postBoth: postBoth && !bankKind(kind) ? true : undefined,
          requestId: requestId || undefined,
        });
        toast.showSuccess('Inter-company transaction initiated');
      } else if (dlg.kind === 'accept') {
        await IntercompanyService.accept(dlg.t.id, { toAccountId: toAccountId || undefined });
        toast.showSuccess('Transaction accepted and posted');
      } else if (dlg.kind === 'withdraw') {
        await IntercompanyService.withdraw(dlg.t.id);
        toast.showSuccess('Transaction withdrawn; your journal is reversed');
      } else if (dlg.kind === 'reject') {
        await IntercompanyService.reject(dlg.t.id, { reason });
        toast.showSuccess('Transaction rejected');
      } else {
        await IntercompanyService.reverse(dlg.t.id, { reason });
        toast.showSuccess('Reversal recorded');
      }
      setDlg(null);
      await loadTxns();
    } catch (e) {
      toast.showApiError(e, 'The action failed');
    } finally {
      setSaving(false);
    }
  };

  const saveMap = async (m: IcMap, patch: Partial<IcMap>) => {
    const n = { ...m, ...patch };
    try {
      await IntercompanyService.saveMap({
        institutionId, counterpartyInstitutionId: n.counterpartyInstitutionId, receivableAccountId: n.receivableAccountId,
        payableAccountId: n.payableAccountId, revenueAccountId: n.revenueAccountId || null, expenseAccountId: n.expenseAccountId || null,
      });
      await loadMaps();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the account map');
    }
  };

  const suggest = async () => {
    try {
      const r = await IntercompanyService.createSuggestedAccounts(institutionId);
      toast.showSuccess(`Created ${r.created.length}, already present ${r.present.length}, skipped ${r.skipped.length}`);
      setMaps(r.maps);
      await loadAccounts();
    } catch (e) {
      toast.showApiError(e, 'Failed to create the suggested accounts');
    }
  };

  const bankAccounts = depositAccountOptions(assets);
  const fromPickerAccounts = bankKind(kind) ? bankAccounts : revenues;

  if (groupLoaded && !group) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>Inter-company</Typography>
        <Alert severity="info">This institution is not in an inter-company group. Ask the platform operator to add it to a group.</Alert>
      </Box>
    );
  }

  return (
    <Box data-help-page="accounting.interco.page" sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Inter-company{group ? ` - ${group.name}` : ''}</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
        <Tab data-help="accounting.interco.tabTransactions" label="Transactions" />
        <Tab data-help="accounting.interco.tabAccountMap" label="Account map" />
        <Tab data-help="accounting.interco.tabReconciliation" label="Reconciliation" />
      </Tabs>

      {tab === 0 && (
        <>
          <FocusedRowChip focusId={focusId} noun="transaction" onClear={clearFocus} />
          <Box data-help-section="accounting.interco.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField data-help="accounting.interco.status" select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={{ flex: '1 1 200px', maxWidth: 280 }}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
            </TextField>
            {canManage && (
              <Button data-help="accounting.interco.new" variant="contained" startIcon={<AddIcon />} onClick={() => open({ kind: 'initiate' })}>New transaction</Button>
            )}
          </Box>
          {loading ? <CircularProgress /> : (
            <TableContainer component={Paper}>
              <Table data-help-section="accounting.interco.table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="accounting.interco.col.number">Number</TableCell><TableCell data-help="accounting.interco.col.date">Date</TableCell><TableCell data-help="accounting.interco.col.kind">Kind</TableCell>
                    <TableCell data-help="accounting.interco.col.from">From</TableCell><TableCell data-help="accounting.interco.col.to">To</TableCell><TableCell data-help="accounting.interco.col.amount" align="right">Amount</TableCell>
                    <TableCell data-help="accounting.interco.col.status">Status</TableCell><TableCell data-help="accounting.interco.col.actions">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && <TableRow><TableCell colSpan={8}>No inter-company transactions yet.</TableCell></TableRow>}
                  {(focusId ? rows.filter(t => t.id === focusId) : rows).map(t => (
                    <TableRow key={t.id}>
                      <TableCell>{t.displayNumber ?? t.txnNumber}</TableCell>
                      <TableCell>{formatDate(t.txnDate)}</TableCell>
                      <TableCell>{humanizeEnum(t.kind)}</TableCell>
                      <TableCell>{t.fromInstitutionName}</TableCell>
                      <TableCell>{t.toInstitutionName}</TableCell>
                      <TableCell align="right">{formatMoney(t.amount)}</TableCell>
                      <TableCell>
                        <Chip size="small" color={COLORS[t.status] ?? 'default'} label={humanizeEnum(t.status)} />
                        {t.reversalPending && <Chip size="small" sx={{ ml: 0.5 }} color="warning" label="Reversal pending" />}
                      </TableCell>
                      <TableCell>
                        {canManage && t.canAccept && <Button data-help="accounting.interco.action.accept" size="small" onClick={() => open({ kind: 'accept', t })}>Accept</Button>}
                        {canManage && t.status === 'PENDING_ACCEPTANCE' && <Button data-help="accounting.interco.action.reject" size="small" color="error" onClick={() => open({ kind: 'reject', t })}>Reject</Button>}
                        {canManage && t.canWithdraw && <Button data-help="accounting.interco.action.withdraw" size="small" color="error" onClick={() => open({ kind: 'withdraw', t })}>Withdraw</Button>}
                        {canManage && t.canReverse && <Button data-help="accounting.interco.action.reverse" size="small" onClick={() => open({ kind: 'reverse', t })}>Reverse</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <Box data-help-section="accounting.interco.mapToolbar" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {canManage && <Button data-help="accounting.interco.suggest" variant="outlined" onClick={suggest}>Create suggested accounts</Button>}
          </Box>
          {counterparties.length === 0 && <Alert severity="info">This group has no other member institutions.</Alert>}
          <TableContainer component={Paper}>
            <Table data-help-section="accounting.interco.mapTable" size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="accounting.interco.map.col.counterparty">Counterparty</TableCell><TableCell data-help="accounting.interco.map.col.receivable">IC receivable</TableCell><TableCell data-help="accounting.interco.map.col.payable">IC payable</TableCell>
                  <TableCell data-help="accounting.interco.map.col.revenue">IC revenue (optional)</TableCell><TableCell data-help="accounting.interco.map.col.expense">IC expense (optional)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {counterparties.map(cp => {
                  const m = maps.find(x => x.counterpartyInstitutionId === cp.institutionId);
                  return (
                    <TableRow key={cp.institutionId}>
                      <TableCell>{cp.institutionName}</TableCell>
                      {m ? (
                        <>
                          <TableCell><AccountAutocomplete data-help="accounting.interco.map.receivable" size="small" label="Receivable" accounts={assets} value={m.receivableAccountId} disabled={!canManage}
                            onChange={id => id && saveMap(m, { receivableAccountId: id })} /></TableCell>
                          <TableCell><AccountAutocomplete data-help="accounting.interco.map.payable" size="small" label="Payable" accounts={liabilities} value={m.payableAccountId} disabled={!canManage}
                            onChange={id => id && saveMap(m, { payableAccountId: id })} /></TableCell>
                          <TableCell><AccountAutocomplete data-help="accounting.interco.map.revenue" size="small" label="Revenue" accounts={revenues} value={m.revenueAccountId ?? ''} disabled={!canManage}
                            onChange={id => saveMap(m, { revenueAccountId: id || null })} /></TableCell>
                          <TableCell><AccountAutocomplete data-help="accounting.interco.map.expense" size="small" label="Expense" accounts={expenses} value={m.expenseAccountId ?? ''} disabled={!canManage}
                            onChange={id => saveMap(m, { expenseAccountId: id || null })} /></TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={4}><Typography variant="body2" color="text.secondary">Not mapped yet. Use "Create suggested accounts".</Typography></TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === 2 && (
        <>
          <Box data-help-section="accounting.interco.reconFilters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField data-help="accounting.interco.reconAsOf" size="small" type="date" label="As of" value={reconAsOf} onChange={e => setReconAsOf(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 200px', maxWidth: 240 }} />
          </Box>
          <TableContainer component={Paper}>
            <Table data-help-section="accounting.interco.reconTable" size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="accounting.interco.recon.col.holder">Holder</TableCell><TableCell data-help="accounting.interco.recon.col.counterparty">Counterparty</TableCell><TableCell data-help="accounting.interco.recon.col.receivable" align="right">Receivable</TableCell>
                  <TableCell data-help="accounting.interco.recon.col.payable" align="right">Payable</TableCell><TableCell data-help="accounting.interco.recon.col.difference" align="right">Difference</TableCell>
                  <TableCell data-help="accounting.interco.recon.col.pending" align="right">Pending explains</TableCell><TableCell data-help="accounting.interco.recon.col.unexplained" align="right">Unexplained</TableCell><TableCell data-help="accounting.interco.recon.col.note">Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(recon?.rows ?? []).length === 0 && <TableRow><TableCell colSpan={8}>No inter-company balances to reconcile.</TableCell></TableRow>}
                {(recon?.rows ?? []).map(r => (
                  <TableRow key={r.holderInstitutionId + r.counterpartyInstitutionId}>
                    <TableCell>{r.holderName}</TableCell><TableCell>{r.counterpartyName}</TableCell>
                    <TableCell align="right">{money(r.receivable)}</TableCell><TableCell align="right">{money(r.payable)}</TableCell>
                    <TableCell align="right">{money(r.difference)}</TableCell><TableCell align="right">{money(r.pendingExplained)}</TableCell>
                    <TableCell align="right">{money(r.unexplained)}</TableCell><TableCell>{r.note ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog data-help-page="accounting.interco.form.page" open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dlg?.kind === 'initiate' ? 'New inter-company transaction'
            : dlg?.kind === 'accept' ? `Accept ${(dlg.t.displayNumber ?? dlg.t.txnNumber)}`
            : dlg?.kind === 'reject' ? `Reject ${(dlg.t.displayNumber ?? dlg.t.txnNumber)}` : dlg?.kind === 'reverse' ? `Reverse ${(dlg.t.displayNumber ?? dlg.t.txnNumber)}`
            : dlg?.kind === 'withdraw' ? `Withdraw ${(dlg.t.displayNumber ?? dlg.t.txnNumber)}` : ''}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {dlg?.kind === 'initiate' && (
            <>
              <SearchableSelect data-help="accounting.interco.form.field.counterparty" options={counterparties} value={to} onChange={id => { setTo(id); fromTouched.current = false; }} label="Counterparty (to)" size="small"
                getOptionId={m => m.institutionId} getOptionLabel={m => m.institutionName} />
              <TextField data-help="accounting.interco.form.field.kind" select size="small" label="Kind" value={kind} onChange={e => { setKind(e.target.value as IcKind); setFromAccountId(''); fromTouched.current = false; }}>
                {KINDS.map(k => <MenuItem key={k} value={k}>{humanizeEnum(k)}</MenuItem>)}
              </TextField>
              <TextField data-help="accounting.interco.form.field.amount" size="small" type="number" label="Amount (BDT)" value={amount} onChange={e => setAmount(e.target.value)} />
              <TextField data-help="accounting.interco.form.field.date" size="small" type="date" label="Date" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              {kind && (
                <AccountAutocomplete data-help="accounting.interco.form.field.fromAccount" size="small" accounts={fromPickerAccounts} value={fromAccountId} onChange={id => { fromTouched.current = true; setFromAccountId(id); }}
                  label={bankKind(kind) ? 'Bank account (money leaves from)' : 'Revenue / recovery account'}
                  placeholder={bankKind(kind) ? undefined : 'use the IC revenue account from the map'}
                  helperText={bankKind(kind) ? undefined : 'Filled from the account map when the counterparty has an IC revenue account.'} />
              )}
              <TextField data-help="accounting.interco.form.field.description" size="small" label="Description" value={description} onChange={e => setDescription(e.target.value)} />
              {canManage && to && (
                <>
                  <FormControlLabel data-help="accounting.interco.form.field.postBoth" control={<Checkbox checked={postBoth} disabled={bankKind(kind)} onChange={e => setPostBoth(e.target.checked)} />}
                    label="Post both sides now" />
                  <FormHelperText>Only if you hold inter-company manage in both institutions. The counterparty side books to its IC expense account; loans and settlements are accepted by the counterparty.</FormHelperText>
                </>
              )}
            </>
          )}
          {dlg?.kind === 'accept' && (
            <>
              <Typography variant="body2">
                {humanizeEnum(dlg.t.kind)} of {formatMoney(dlg.t.amount)} from {dlg.t.fromInstitutionName}, dated {formatDate(dlg.t.txnDate)}.
              </Typography>
              <AccountAutocomplete data-help="accounting.interco.form.field.toAccount" size="small" accounts={bankKind(dlg.t.kind) ? bankAccounts : [...expenses, ...assets]} value={toAccountId} onChange={setToAccountId}
                label={bankKind(dlg.t.kind) ? 'Bank account (money arrives in)' : 'Expense or asset account'}
                placeholder={bankKind(dlg.t.kind) ? undefined : 'use the IC expense account from the map'} />
            </>
          )}
          {dlg?.kind === 'withdraw' && (
            <Typography variant="body2">
              {dlg.t.toInstitutionName} rejected this transaction ({dlg.t.rejectedReason}). Withdrawing posts the reversal of your journal.
            </Typography>
          )}
          {(dlg?.kind === 'reject' || dlg?.kind === 'reverse') && (
            <TextField data-help="accounting.interco.form.field.reason" size="small" label="Reason" required multiline minRows={2} value={reason} onChange={e => setReason(e.target.value)}
              helperText={dlg.kind === 'reverse' ? 'Each side requests its own reversal; both journals reverse once both have asked.' : undefined} />
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="accounting.interco.form.cancel" onClick={() => setDlg(null)}>Cancel</Button>
          <Button data-help="accounting.interco.form.confirm" variant="contained" disabled={!valid || saving} onClick={submit}>{saving ? 'Saving...' : 'Confirm'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Intercompany;
